import { observer } from 'mobx-react-lite';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../../global-state/themeContext';
import { SortDirection } from '../../../global-state/types';
import { SalesEfficiencyViewItem, SalesEfficiencyViewResponse } from '../../../hooks';
import { CurrencyFormatterOptions } from '../../../hooks/useCurrencyTools';
import { useFilteredDataByArea } from '../../../hooks/useFilteredData';
import { Typography } from '../../../stories/atoms';
import { ColumnFlexContainer } from '../../../stories/atoms/containerV2/ContainerV2';
import { Animation, Animations } from '../../../stories/molecules';
import { HeaderRowItem, Table } from '../../../stories/molecules/tablev2/Table';
import { Colors, muiTheme } from '../../../theme';
import { commonTranslations, homeTranslations } from '../../../translations';
import { areaResultTranslations } from '../../../translations/areaResult';
import { formatAmountWithSeparator } from '../../../utils';
import { calculateColor, calculateIndexValue, calculateTrend } from '../../../utils/budgetPlanned';
import { AreaResultHeaderTooltip, SummaryType } from '../areaResultHeaderTooltip/AreaResultHeaderTooltip';

export type SalesEfficiencyViewProps = {
  isLoading: boolean;
  isError: boolean;
  storesByArea: string[];
  salesEfficiencyData: SalesEfficiencyViewResponse | null;
  selectedStores: string[];
  formatCurrency: (value: number, options?: CurrencyFormatterOptions) => string;
};

enum SalesEfficiencyColumnType {
  StoreId = 'storeId',
  Sales = 'sales',
  SalesLastWeek = 'salesLastWeek',
  SalesLastYear = 'salesLastYear',
  SalesBudget = 'salesBudget',
  ConversionRate = 'conversionRate',
  ConversionRateLastWeek = 'conversionRateLastWeek',
  ConversionRateLastYear = 'conversionRateLastYear',
  Takt1 = 'takt1',
  Takt1LastWeek = 'takt1LastWeek',
  Takt1LastYear = 'takt1LastYear',
  Takt1Budget = 'takt1Budget',
  Spt = 'spt',
  SptLastWeek = 'sptLastWeek',
  SptLastYear = 'sptLastYear',
  SptBudget = 'sptBudget',
  EffectiveHours = 'effectiveHours',
  EffectiveHoursLastWeek = 'effectiveHoursLastWeek',
  EffectiveHoursLastYear = 'effectiveHoursLastYear',
  EffectiveHoursBudget = 'effectiveHoursBudget',
}

export const SalesEfficiencyView: FC<SalesEfficiencyViewProps> = observer(
  ({ isLoading, isError, storesByArea, salesEfficiencyData, selectedStores, formatCurrency }) => {
    const { t } = useTranslation();
    const { salesEfficiency } = areaResultTranslations;
    const { mode } = useThemeContext();

    const isDarkMode = mode === 'dark';
    const themeBorderColor = isDarkMode ? muiTheme.color.borderGrayDark : muiTheme.color.borderGray;
    const headerBorderBottomColor = isDarkMode ? muiTheme.color.primary : muiTheme.color.borderGray;

    const [sortColumnId, setSortColumnId] = useState<SalesEfficiencyColumnType | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection | null>(null);

    const headerFooterSxProps = {
      padding: '12px 8px',
      ':nth-of-type(5),:nth-of-type(8)': {
        border: `1px solid ${themeBorderColor}`,
        borderLeft: 'none',
        borderTop: 'none',
        borderBottom: `1px solid ${headerBorderBottomColor}`,
      },
    };

    const filteredSalesData = useFilteredDataByArea(
      salesEfficiencyData?.salesAndEfficiency,
      storesByArea,
      selectedStores,
    );

    const headerItems: HeaderRowItem<SalesEfficiencyColumnType>[] = [
      {
        id: SalesEfficiencyColumnType.StoreId,
        content: (
          <Typography>
            {t(areaResultTranslations.storeId.key, areaResultTranslations.storeId.defaultValue)}
          </Typography>
        ),
      },
      {
        id: SalesEfficiencyColumnType.Sales,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(salesEfficiency.sales.key, salesEfficiency.sales.defaultValue)}
            tooltipText={t(
              homeTranslations.hourlyTurnover.toolTip.key,
              homeTranslations.hourlyTurnover.toolTip.defaultValue,
            )}
            summaryType={SummaryType.SUM}
            isBold
          />
        ),
      },
      {
        id: SalesEfficiencyColumnType.SalesLastWeek,
        content: <Typography>LW</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.SalesLastYear,
        content: <Typography>LY</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.SalesBudget,
        content: <Typography>BU</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.ConversionRate,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(salesEfficiency.conversionRate.key, salesEfficiency.conversionRate.defaultValue)}
            tooltipText={t(
              homeTranslations.conversionRate.toolTip.key,
              homeTranslations.conversionRate.toolTip.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
            isBold
          />
        ),
      },
      {
        id: SalesEfficiencyColumnType.ConversionRateLastWeek,
        content: <Typography>LW</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.ConversionRateLastYear,
        content: <Typography>LY</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.Takt1,
        content: (
          <ColumnFlexContainer>
            <Typography type="button">
              {t(
                homeTranslations.efficiencyWidget.title.key,
                homeTranslations.efficiencyWidget.title.defaultValue,
              )}
            </Typography>
            <AreaResultHeaderTooltip
              headerText={t(salesEfficiency.takt1.key, salesEfficiency.takt1.defaultValue)}
              tooltipText={t(
                homeTranslations.efficiencyWidget.takt1.text.key,
                homeTranslations.efficiencyWidget.takt1.text.defaultValue,
              )}
              summaryType={SummaryType.GROSSAVG}
            />
          </ColumnFlexContainer>
        ),
      },
      {
        id: SalesEfficiencyColumnType.Takt1LastWeek,
        content: <Typography>LW</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.Takt1LastYear,
        content: <Typography>LY</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.Takt1Budget,
        content: <Typography>BU</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.Spt,
        content: (
          <AreaResultHeaderTooltip
            headerText="SPT"
            tooltipText={t(
              homeTranslations.efficiencyWidget.spt.text.key,
              homeTranslations.efficiencyWidget.spt.text.defaultValue,
            )}
            summaryType={SummaryType.GROSSAVG}
          />
        ),
      },
      {
        id: SalesEfficiencyColumnType.SptLastWeek,
        content: <Typography>LW</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.SptLastYear,
        content: <Typography>LY</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.SptBudget,
        content: <Typography>BU</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.EffectiveHours,
        content: (
          <AreaResultHeaderTooltip
            headerText={t(
              homeTranslations.efficiencyWidget.effectiveHours.title.key,
              homeTranslations.efficiencyWidget.effectiveHours.title.defaultValue,
            )}
            tooltipText={t(
              homeTranslations.efficiencyWidget.effectiveHours.text.key,
              homeTranslations.efficiencyWidget.effectiveHours.text.defaultValue,
            )}
            summaryType={SummaryType.SUM}
          />
        ),
      },
      {
        id: SalesEfficiencyColumnType.EffectiveHoursLastWeek,
        content: <Typography>LW</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.EffectiveHoursLastYear,
        content: <Typography>LY</Typography>,
      },
      {
        id: SalesEfficiencyColumnType.EffectiveHoursBudget,
        content: <Typography>BU</Typography>,
      },
    ];

    const isNoData = useMemo(() => {
      return !salesEfficiencyData || !filteredSalesData?.length;
    }, [salesEfficiencyData, filteredSalesData, storesByArea]);

    const computeIndex = (originalAmount: number, previousAmount: number, isEffectiveHours: boolean) => {
      let comparedValue = previousAmount;

      if (isEffectiveHours) {
        comparedValue = previousAmount < 1 ? 0 : previousAmount;
      }

      const indexValue = calculateIndexValue(originalAmount, comparedValue);

      const trend = calculateTrend(originalAmount, comparedValue, isEffectiveHours);

      const color: Colors = calculateColor(trend);

      return {
        indexValue,
        textColor: color,
      };
    };

    const getTableItem = (item: SalesEfficiencyViewItem, isSummary: boolean) => {
      const typographyType = isSummary ? 'body3' : 'body2';

      const indexMetrics = {
        SalesLastWeek: computeIndex(item.sales, item.salesLastWeek, false),
        SalesLastYear: computeIndex(item.sales, item.salesLastYear, false),
        SalesBudget: computeIndex(item.sales, item.salesBudget, false),
        ConversionRateLastWeek: computeIndex(item.conversionRate, item.conversionRateLastWeek, false),
        ConversionRateLastYear: computeIndex(item.conversionRate, item.conversionRateLastYear, false),
        Takt1LastWeek: computeIndex(item.takt1, item.takt1LastWeek, false),
        Takt1LastYear: computeIndex(item.takt1, item.takt1LastYear, false),
        Takt1Budget: computeIndex(item.takt1, item.takt1Budget, false),
        SptLastWeek: computeIndex(item.spt, item.sptLastWeek, false),
        SptLastYear: computeIndex(item.spt, item.sptLastYear, false),
        SptBudget: computeIndex(item.spt, item.sptBudget, false),
        EffectiveHoursLastWeek: computeIndex(item.effectiveHours, item.effectiveHoursLastWeek, true),
        EffectiveHoursLastYear: computeIndex(item.effectiveHours, item.effectiveHoursLastYear, true),
        EffectiveHoursBudget: computeIndex(item.effectiveHours, item.effectiveHoursBudget, true),
      };

      return [
        {
          id: SalesEfficiencyColumnType.StoreId,
          width: '10%',
          content: (
            <Typography type={typographyType}>
              {isSummary
                ? t(areaResultTranslations.summary.key, areaResultTranslations.summary.defaultValue)
                : item.storeId}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Sales,
          content: (
            <Typography type={typographyType}>
              {formatCurrency(item.sales, { showCurrencyCode: isSummary })}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.SalesLastWeek,
          content: (
            <Typography type={typographyType} color={indexMetrics.SalesLastWeek.textColor}>
              {indexMetrics.SalesLastWeek.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.SalesLastYear,
          content: (
            <Typography type={typographyType} color={indexMetrics.SalesLastYear.textColor}>
              {indexMetrics.SalesLastYear.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.SalesBudget,
          content: (
            <Typography type={typographyType} color={indexMetrics.SalesBudget.textColor}>
              {indexMetrics.SalesBudget.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.ConversionRate,
          width: '10%',
          content: (
            <Typography type={typographyType}>{formatAmountWithSeparator(item.conversionRate, '%')}</Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.ConversionRateLastWeek,
          content: (
            <Typography type={typographyType} color={indexMetrics.ConversionRateLastWeek.textColor}>
              {indexMetrics.ConversionRateLastWeek.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.ConversionRateLastYear,
          content: (
            <Typography type={typographyType} color={indexMetrics.ConversionRateLastYear.textColor}>
              {indexMetrics.ConversionRateLastYear.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Takt1,
          content: (
            <Typography type={typographyType}>
              {formatCurrency(item.takt1, { showCurrencyCode: false })}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Takt1LastWeek,
          content: (
            <Typography type={typographyType} color={indexMetrics.Takt1LastWeek.textColor}>
              {indexMetrics.Takt1LastWeek.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Takt1LastYear,
          content: (
            <Typography type={typographyType} color={indexMetrics.Takt1LastYear.textColor}>
              {indexMetrics.Takt1LastYear.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Takt1Budget,
          content: (
            <Typography type={typographyType} color={indexMetrics.Takt1Budget.textColor}>
              {indexMetrics.Takt1Budget.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.Spt,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.spt)}</Typography>,
        },
        {
          id: SalesEfficiencyColumnType.SptLastWeek,
          content: (
            <Typography type={typographyType} color={indexMetrics.SptLastWeek.textColor}>
              {indexMetrics.SptLastWeek.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.SptLastYear,
          content: (
            <Typography type={typographyType} color={indexMetrics.SptLastYear.textColor}>
              {indexMetrics.SptLastYear.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.SptBudget,
          content: (
            <Typography type={typographyType} color={indexMetrics.SptBudget.textColor}>
              {indexMetrics.SptBudget.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.EffectiveHours,
          content: <Typography type={typographyType}>{formatAmountWithSeparator(item.effectiveHours)}</Typography>,
        },
        {
          id: SalesEfficiencyColumnType.EffectiveHoursLastWeek,
          content: (
            <Typography type={typographyType} color={indexMetrics.EffectiveHoursLastWeek.textColor}>
              {indexMetrics.EffectiveHoursLastWeek.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.EffectiveHoursLastYear,
          content: (
            <Typography type={typographyType} color={indexMetrics.EffectiveHoursLastYear.textColor}>
              {indexMetrics.EffectiveHoursLastYear.indexValue}
            </Typography>
          ),
        },
        {
          id: SalesEfficiencyColumnType.EffectiveHoursBudget,
          content: (
            <Typography type={typographyType} color={indexMetrics.EffectiveHoursBudget.textColor}>
              {indexMetrics.EffectiveHoursBudget.indexValue}
            </Typography>
          ),
        },
      ];
    };

    const getFooterItems = () => {
      if (selectedStores.length !== 0 || !salesEfficiencyData?.summary) {
        return [];
      }
      return getTableItem({ storeId: 'Summary', ...salesEfficiencyData?.summary }, true);
    };

    const onColumnSort = (columnType: SalesEfficiencyColumnType) => {
      if (columnType !== sortColumnId) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      if (sortDirection === SortDirection.Ascending) {
        setSortColumnId(columnType);
        setSortDirection(SortDirection.Descending);
        return;
      }

      setSortColumnId(columnType);
      setSortDirection(SortDirection.Ascending);
    };

    return (
      <>
        {isLoading && (
          <ColumnFlexContainer flex={1} data-testid="sales-efficiency-loading-view">
            <Animation
              title={t(salesEfficiency.loading.key, salesEfficiency.loading.defaultValue)}
              animation={Animations.LOADING}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && isError && (
          <ColumnFlexContainer flex={1} data-testid="sales-efficiency-error-view">
            <Animation
              title={t(commonTranslations.errorText1.key, commonTranslations.errorText1.defaultValue)}
              subtitle={t(commonTranslations.errorText2.key, commonTranslations.errorText2.defaultValue)}
              animation={Animations.SAD}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && isNoData && (
          <ColumnFlexContainer flex={1} data-testid="sales-efficiency-no-data-view">
            <Animation
              title={t(salesEfficiency.noData.key, salesEfficiency.noData.defaultValue)}
              animation={Animations.CONFUSED}
            />
          </ColumnFlexContainer>
        )}

        {!isLoading && !isError && !isNoData && (
          <ColumnFlexContainer flex={1} padding={[4, 0]} minHeight={[0]} data-testid="sales-efficiency-table">
            <Table
              items={filteredSalesData}
              headerItems={headerItems}
              stickyHeader
              stickyFirstColumn
              disableHover
              sortConfig={{
                storeId: { getItemValue: (item) => item.storeId },
                sales: { getItemValue: (item) => item.sales },
                conversionRate: { getItemValue: (item) => item.conversionRate },
                takt1: { getItemValue: (item) => item.takt1 },
                spt: { getItemValue: (item) => item.spt },
                effectiveHours: { getItemValue: (item) => item.effectiveHours },
              }}
              sortColumnId={sortColumnId || undefined}
              onSort={onColumnSort}
              sortDirection={sortDirection || undefined}
              sxProps={{
                padding: '12px 8px',
                ':nth-of-type(5),:nth-of-type(8)': {
                  border: `1px solid ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                },
                ':nth-of-type(12),:nth-of-type(16)': {
                  border: `1px dashed ${themeBorderColor}`,
                  borderLeft: 'none',
                  borderTop: 'none',
                  borderBottom: `1px solid ${themeBorderColor}`,
                },
              }}
              headerSxProps={headerFooterSxProps}
              footerSxProps={headerFooterSxProps}
              getRowItems={(item) => {
                return getTableItem(item, false);
              }}
              footerItems={getFooterItems()}
            />
          </ColumnFlexContainer>
        )}
      </>
    );
  },
);
